import { z } from 'zod';

/**
 * The period the console is reading, in the address.
 *
 * Mirrors the workspace Usage route's own schema, deliberately rather than by import:
 * the two are the same rule and a malformed value falls back to the current period
 * instead of reaching the service and coming back a 422, which would show an error page
 * for what is really a bad bookmark.
 *
 * All four grammars, not just the calendar month: `YYYY-MM`, `daily:YYYY-MM-DD`,
 * `weekly:YYYY-MM-DD` and the literal `none`. The service's `parse_period_key` resolves
 * a key from its own grammar and `_resolve_period` is shared by the workspace and
 * organization reads alike (spec 085), so a console restricted to months would refuse
 * to *display* periods the service is perfectly willing to report on — and the key
 * carries its own type, which is why no second search parameter is needed to say which
 * kind of period is being viewed.
 *
 * It lives here rather than inline in a route module because **two** admin routes need
 * it — the overview and the limits table both read period-scoped figures, and two
 * copies is how they end up disagreeing about what a valid period looks like.
 */
export const adminUsageSearchSchema = z.object({
  period: z
    .string()
    .regex(
      /^(\d{4}-(0[1-9]|1[0-2])|daily:\d{4}-\d{2}-\d{2}|weekly:\d{4}-\d{2}-\d{2}|none)$/,
    )
    .optional()
    .catch(undefined),
});

export type AdminUsageSearch = z.infer<typeof adminUsageSearchSchema>;

/** The column orders the Limits table offers, as they appear in the address. */
const LIMITS_SORTS = [
  'name.asc',
  'name.desc',
  'spend.asc',
  'spend.desc',
  'runs.asc',
  'runs.desc',
] as const;
export type LimitsSortParam = (typeof LIMITS_SORTS)[number];

/**
 * The Limits page's view, in the address (spec 117 R3): the period the spend figures
 * cover, the search, the filter, the column order and the workspace whose sheet is open.
 * In the address so the back button closes the sheet and other surfaces can link
 * straight to one workspace's limits (FR-017).
 *
 * `q` is coerced from any primitive: the router parses `?q=10` as the number 10, and a
 * string-only schema would drop that search without a word.
 */
export const limitsSearchSchema = adminUsageSearchSchema.extend({
  q: z
    .union([z.string(), z.number(), z.boolean()])
    .transform(String)
    .optional()
    .catch(undefined),
  filter: z
    .enum(['all', 'near-spend', 'run-full', 'waiting', 'custom', 'caps'])
    .optional()
    .catch(undefined),
  sort: z.enum(LIMITS_SORTS).optional().catch(undefined),
  workspace: z.string().min(1).optional().catch(undefined),
});

export type LimitsSearch = z.infer<typeof limitsSearchSchema>;

export function parseSort(
  param: LimitsSortParam | undefined,
): { key: 'name' | 'spend' | 'runs'; dir: 'asc' | 'desc' } | undefined {
  if (!param) return undefined;
  const [key, dir] = param.split('.') as [
    'name' | 'spend' | 'runs',
    'asc' | 'desc',
  ];
  return { key, dir };
}

export function sortParam(sort: {
  key: 'name' | 'spend' | 'runs';
  dir: 'asc' | 'desc';
}): LimitsSortParam {
  return `${sort.key}.${sort.dir}`;
}
